import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { STAFF_FEATURE_GROUPS } from '../config/staffFeatures';
import { filterValidStaffFeatureIds } from '../utils/staffAccess';
import { forceSignOutStaff } from '../utils/staffSessionGuard';
import Notification from './Notification';
import './AdminDashboard.css';
import './AdminStaffUsersPage.css';

function mapCallableError(err) {
  const code = err?.code || '';
  const detail = err?.details || err?.message || '';
  if (code === 'functions/unauthenticated' || code === 'unauthenticated') {
    return 'Sesión vencida: volvé a iniciar sesión.';
  }
  if (code === 'functions/permission-denied' || code === 'permission-denied') {
    return 'No tenés permiso para esta acción.';
  }
  if (code === 'functions/already-exists' || code === 'already-exists') {
    return 'Ya existe una cuenta con ese email.';
  }
  if (code === 'functions/invalid-argument' || code === 'invalid-argument') {
    return typeof detail === 'string' ? detail : 'Datos inválidos.';
  }
  if (code === 'functions/failed-precondition' || code === 'failed-precondition') {
    return typeof detail === 'string' ? detail : 'No se puede completar la acción.';
  }
  if (typeof detail === 'string' && detail.length > 0) return detail;
  return 'Ocurrió un error. Probá de nuevo o revisá que las funciones estén desplegadas.';
}

const ALL_IDS = STAFF_FEATURE_GROUPS.flatMap((g) => g.items.map((i) => i.id));

function PermissionEditor({ selected, onChange, disabled }) {
  return (
    <div className="admin-staff-page__perm-groups">
      {STAFF_FEATURE_GROUPS.map((group) => (
        <div key={group.title} className="admin-staff-page__perm-group">
          <h3>{group.title}</h3>
          <div className="admin-staff-page__perm-grid">
            {group.items.map((item) => (
              <label key={item.id} className="admin-staff-page__perm-item">
                <input
                  type="checkbox"
                  checked={selected.includes(item.id)}
                  disabled={disabled}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...new Set([...selected, item.id])]);
                    } else {
                      onChange(selected.filter((x) => x !== item.id));
                    }
                  }}
                />
                <span>
                  {item.label}
                  {item.description ? <small>{item.description}</small> : null}
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="admin-staff-page__perm-actions">
        <button type="button" disabled={disabled} onClick={() => onChange([...ALL_IDS])}>
          Marcar todo
        </button>
        <button type="button" disabled={disabled} onClick={() => onChange([])}>
          Quitar todo
        </button>
      </div>
    </div>
  );
}

const AdminStaffUsersPage = () => {
  const [notification, setNotification] = useState({ message: '', show: false });
  const showMessage = useCallback((message) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3200);
  }, []);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    displayName: '',
    accountType: 'limited',
    selectedFeatures: ['localSales']
  });
  const [passwordEdits, setPasswordEdits] = useState({});
  const [displayNameEdits, setDisplayNameEdits] = useState({});
  const [permModal, setPermModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const ensureCurrentAdminIsActive = useCallback(async () => {
    try {
      const profileFn = httpsCallable(functions, 'staffGetOwnProfile');
      const res = await profileFn({});
      const profile = res.data || {};
      const isActiveAdmin =
        profile.exists === true &&
        profile.role === 'admin' &&
        profile.authDisabled !== true;

      if (isActiveAdmin) {
        return true;
      }

      await forceSignOutStaff(profile.authDisabled === true ? 'disabled' : 'permission');
      return false;
    } catch (err) {
      const code = err?.code || '';
      if (
        code === 'functions/unauthenticated' ||
        code === 'unauthenticated' ||
        code === 'functions/permission-denied' ||
        code === 'permission-denied'
      ) {
        await forceSignOutStaff(code.includes('unauthenticated') ? 'session' : 'permission');
        return false;
      }
      showMessage(mapCallableError(err));
      return false;
    }
  }, [showMessage]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listFn = httpsCallable(functions, 'adminListStaffUsers');
      const res = await listFn({});
      const list = res.data?.users || [];
      setUsers(list.sort((a, b) => (a.email || '').localeCompare(b.email || '')));
    } catch (e) {
      console.error(e);
      showMessage(mapCallableError(e));
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      showMessage('Completá email y contraseña.');
      return;
    }
    const isAdmin = form.accountType === 'admin';
    const isViewer = form.accountType === 'readonly';
    const feats = filterValidStaffFeatureIds(form.selectedFeatures);
    if (!isAdmin && feats.length === 0) {
      showMessage(
        isViewer
          ? 'Elegí al menos un acceso para el usuario de solo lectura.'
          : 'Elegí al menos un acceso para el personal limitado.'
      );
      return;
    }
    if (!(await ensureCurrentAdminIsActive())) return;
    setSaving(true);
    try {
      const createFn = httpsCallable(functions, 'adminCreateStaffUser');
      await createFn({
        email: form.email.trim(),
        password: form.password,
        role: isAdmin ? 'admin' : isViewer ? 'viewer' : 'staff',
        displayName: form.displayName.trim(),
        allowedFeatures: isAdmin ? [] : feats
      });
      showMessage(`Usuario creado: ${form.email.trim()}`);
      setForm({
        email: '',
        password: '',
        displayName: '',
        accountType: 'limited',
        selectedFeatures: ['localSales']
      });
      await load();
    } catch (err) {
      showMessage(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (uid, newRole) => {
    if (!(await ensureCurrentAdminIsActive())) return;
    setSaving(true);
    try {
      const updateFn = httpsCallable(functions, 'adminUpdateStaffUser');
      const row = users.find((x) => x.uid === uid);
      const existing = filterValidStaffFeatureIds(row?.allowedFeatures);
      const payload = { uid, role: newRole };
      if (newRole === 'admin') {
        payload.allowedFeatures = [];
      } else if (newRole === 'staff' || newRole === 'viewer') {
        payload.allowedFeatures = existing.length ? existing : ['localSales'];
      }
      await updateFn(payload);
      showMessage('Rol actualizado.');
      await load();
    } catch (err) {
      showMessage(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleDisabled = async (uid, currentlyDisabled) => {
    if (!(await ensureCurrentAdminIsActive())) return;
    setSaving(true);
    try {
      const updateFn = httpsCallable(functions, 'adminUpdateStaffUser');
      await updateFn({ uid, disabled: !currentlyDisabled });
      showMessage(currentlyDisabled ? 'Usuario reactivado.' : 'Usuario desactivado.');
      await load();
    } catch (err) {
      showMessage(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitDisplayName = async (uid) => {
    const row = users.find((x) => x.uid === uid);
    const raw =
      displayNameEdits[uid] !== undefined ? displayNameEdits[uid] : row?.displayName || '';
    const next = typeof raw === 'string' ? raw.trim() : '';
    const prev = (row?.displayName || '').trim();
    if (next === prev) {
      showMessage('Sin cambios en el nombre.');
      return;
    }
    if (!(await ensureCurrentAdminIsActive())) return;
    setSaving(true);
    try {
      const updateFn = httpsCallable(functions, 'adminUpdateStaffUser');
      await updateFn({ uid, displayName: next });
      showMessage('Nombre actualizado.');
      setDisplayNameEdits((prevEdits) => {
        const n = { ...prevEdits };
        delete n[uid];
        return n;
      });
      await load();
    } catch (err) {
      showMessage(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const submitPassword = async (uid) => {
    const pw = (passwordEdits[uid] || '').trim();
    if (pw.length < 6) {
      showMessage('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (!(await ensureCurrentAdminIsActive())) return;
    setSaving(true);
    try {
      const setPw = httpsCallable(functions, 'adminSetStaffPassword');
      await setPw({ uid, password: pw });
      showMessage('Contraseña actualizada.');
      setPasswordEdits((prev) => ({ ...prev, [uid]: '' }));
      await load();
    } catch (err) {
      showMessage(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (user) => {
    setDeleteConfirmText('');
    setDeleteModal({
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      role: user.role || ''
    });
  };

  const closeDeleteModal = () => {
    if (saving) return;
    setDeleteModal(null);
    setDeleteConfirmText('');
  };

  const confirmDeleteUser = async () => {
    if (!deleteModal) return;
    const expected = (deleteModal.email || '').trim().toLowerCase();
    const typed = deleteConfirmText.trim().toLowerCase();
    if (expected && typed !== expected) {
      showMessage('Escribí el email exacto del usuario para confirmar la eliminación.');
      return;
    }
    if (!(await ensureCurrentAdminIsActive())) return;
    setSaving(true);
    try {
      const deleteFn = httpsCallable(functions, 'adminUpdateStaffUser');
      await deleteFn({
        uid: deleteModal.uid,
        action: 'delete',
        confirmEmail: deleteModal.email
      });
      showMessage(`Usuario eliminado: ${deleteModal.email || deleteModal.uid}`);
      setDeleteModal(null);
      setDeleteConfirmText('');
      await load();
    } catch (err) {
      showMessage(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveModalPermissions = async () => {
    if (!permModal) return;
    const feats = filterValidStaffFeatureIds(permModal.draft);
    if (permModal.role !== 'admin' && feats.length === 0) {
      showMessage('Debe quedar al menos un permiso.');
      return;
    }
    if (!(await ensureCurrentAdminIsActive())) return;
    setSaving(true);
    try {
      const updateFn = httpsCallable(functions, 'adminUpdateStaffUser');
      await updateFn({
        uid: permModal.uid,
        allowedFeatures: permModal.role === 'admin' ? [] : feats
      });
      showMessage('Permisos guardados.');
      setPermModal(null);
      await load();
    } catch (err) {
      showMessage(mapCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (r) => {
    if (r === 'admin') return 'Administrador';
    if (r === 'staff') return 'Personal (accesos)';
    if (r === 'viewer') return 'Solo lectura';
    return r || '—';
  };

  const summarizeFeatures = useMemo(
    () => (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return '—';
      if (arr.length > 4) return `${arr.length} permisos`;
      return arr.join(', ');
    },
    []
  );

  return (
    <div className="admin-dashboard admin-staff-page">
      <Notification message={notification.message} show={notification.show} />

      <div className="admin-staff-page__toolbar">
        <h1>👤 Usuarios, roles y accesos</h1>
        <Link to="/admin" className="admin-staff-page__back">
          ← Volver al panel
        </Link>
      </div>

      <form className="admin-staff-page__form" onSubmit={handleCreate}>
        <h2>Nuevo usuario</h2>
        <div className="admin-staff-page__fields">
          <label>
            Email
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              autoComplete="off"
            />
          </label>
          <label>
            Contraseña
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </label>
          <label>
            Nombre / etiqueta (opcional)
            <input
              type="text"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="Ej. Marketing, Ventas, Depósito…"
              autoComplete="off"
            />
          </label>
          <label>
            Tipo de cuenta
            <select
              value={form.accountType}
              onChange={(e) => setForm((f) => ({ ...f, accountType: e.target.value }))}
            >
              <option value="limited">Personal con accesos limitados</option>
              <option value="readonly">Solo lectura (consulta, sin modificar datos)</option>
              <option value="admin">Administrador completo</option>
            </select>
          </label>
        </div>

        {(form.accountType === 'limited' || form.accountType === 'readonly') && (
          <PermissionEditor
            selected={form.selectedFeatures}
            onChange={(next) => setForm((f) => ({ ...f, selectedFeatures: next }))}
            disabled={saving}
          />
        )}

        <button type="submit" className="admin-staff-page__submit admin-staff-page__submit--spaced" disabled={saving}>
          {saving ? 'Guardando…' : 'Crear usuario'}
        </button>
      </form>

      <div className="admin-staff-page__table-wrap">
        <div className="admin-staff-page__table-header">
          <h2>Usuarios registrados</h2>
          <span className="admin-staff-page__table-count">
            {loading ? '' : `${users.length} ${users.length === 1 ? 'usuario' : 'usuarios'}`}
          </span>
        </div>
        {loading ? (
          <p className="admin-staff-page__table-empty">Cargando…</p>
        ) : users.length === 0 ? (
          <p className="admin-staff-page__table-empty">No hay usuarios o no se pudo cargar la lista.</p>
        ) : (
          <div className="admin-staff-page__table-scroll">
            <table className="admin-staff-page__table">
              <thead>
                <tr>
                  <th className="admin-staff-page__col--email">Email</th>
                  <th className="admin-staff-page__col--name">Nombre / etiqueta</th>
                  <th className="admin-staff-page__col--role">Rol</th>
                  <th className="admin-staff-page__col--access">Accesos</th>
                  <th className="admin-staff-page__col--status">Estado</th>
                  <th className="admin-staff-page__col--password">Nueva contraseña</th>
                  <th className="admin-staff-page__col--actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.uid} className={u.disabled ? 'is-disabled' : ''}>
                    <td className="admin-staff-page__cell--email" title={u.email || ''}>
                      <span className="admin-staff-page__email-text">{u.email || '—'}</span>
                    </td>
                    <td>
                      <div className="admin-staff-page__pw-row admin-staff-page__pw-row--name">
                        <input
                          type="text"
                          placeholder="Ej. Marketing, Ventas…"
                          value={
                            displayNameEdits[u.uid] !== undefined
                              ? displayNameEdits[u.uid]
                              : u.displayName || ''
                          }
                          onChange={(e) =>
                            setDisplayNameEdits((prev) => ({ ...prev, [u.uid]: e.target.value }))
                          }
                          disabled={saving}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="admin-staff-page__btn admin-staff-page__btn--save"
                          onClick={() => submitDisplayName(u.uid)}
                          disabled={saving}
                        >
                          Guardar
                        </button>
                      </div>
                    </td>
                    <td>
                      <select
                        className="admin-staff-page__role-select"
                        value={
                          u.role === 'admin' || u.role === 'staff' || u.role === 'viewer'
                            ? u.role
                            : 'staff'
                        }
                        onChange={(e) => handleRoleChange(u.uid, e.target.value)}
                        disabled={saving}
                      >
                        <option value="staff">{roleLabel('staff')}</option>
                        <option value="viewer">{roleLabel('viewer')}</option>
                        <option value="admin">{roleLabel('admin')}</option>
                      </select>
                    </td>
                    <td className="admin-staff-page__feature-tags">
                      {u.role === 'admin' ? (
                        <span className="admin-staff-page__feature-all">Todo el panel</span>
                      ) : (
                        <>
                          {(() => {
                            const summary = summarizeFeatures(u.allowedFeatures);
                            return summary === '—' ? (
                              <span className="admin-staff-page__feature-empty">Sin accesos</span>
                            ) : (
                              <span
                                className="admin-staff-page__feature-summary"
                                title={
                                  Array.isArray(u.allowedFeatures)
                                    ? u.allowedFeatures.join(', ')
                                    : ''
                                }
                              >
                                {summary}
                              </span>
                            );
                          })()}
                          <div className="admin-staff-page__feature-edit-wrap">
                            <button
                              type="button"
                              className="admin-staff-page__btn admin-staff-page__btn--edit-access"
                              disabled={saving || u.role === 'admin'}
                              onClick={() =>
                                setPermModal({
                                  uid: u.uid,
                                  email: u.email,
                                  displayName: u.displayName || '',
                                  role: u.role,
                                  draft: filterValidStaffFeatureIds(u.allowedFeatures).length
                                    ? filterValidStaffFeatureIds(u.allowedFeatures)
                                    : ['localSales']
                                })
                              }
                            >
                              Editar accesos
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                    <td>
                      <span
                        className={`admin-staff-page__status admin-staff-page__status--${
                          u.disabled ? 'off' : 'on'
                        }`}
                      >
                        {u.disabled ? 'Desactivado' : 'Activo'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-staff-page__pw-row">
                        <input
                          type="password"
                          placeholder="Mín. 6 caracteres"
                          value={passwordEdits[u.uid] || ''}
                          onChange={(e) =>
                            setPasswordEdits((prev) => ({ ...prev, [u.uid]: e.target.value }))
                          }
                          disabled={saving}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="admin-staff-page__btn admin-staff-page__btn--save"
                          onClick={() => submitPassword(u.uid)}
                          disabled={saving}
                        >
                          Guardar clave
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="admin-staff-page__actions-cell">
                        <button
                          type="button"
                          className={`admin-staff-page__btn admin-staff-page__btn--toggle admin-staff-page__btn--toggle-${
                            u.disabled ? 'on' : 'off'
                          }`}
                          onClick={() => toggleDisabled(u.uid, u.disabled)}
                          disabled={saving}
                        >
                          {u.disabled ? 'Reactivar' : 'Desactivar'}
                        </button>
                        <button
                          type="button"
                          className="admin-staff-page__btn admin-staff-page__btn--danger"
                          onClick={() => openDeleteModal(u)}
                          disabled={saving}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteModal && (
        <div className="admin-staff-page__modal-overlay" onClick={closeDeleteModal}>
          <div
            className="admin-staff-page__modal admin-staff-page__modal--danger"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-staff-page__modal-header">
              <h3>Eliminar usuario</h3>
              <button
                type="button"
                className="admin-staff-page__modal-close"
                onClick={closeDeleteModal}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <div className="admin-staff-page__modal-body">
              <p className="admin-staff-page__modal-text">
                Vas a eliminar <strong>{deleteModal.email || deleteModal.uid}</strong>
                {deleteModal.displayName ? ` (${deleteModal.displayName})` : ''}.
              </p>
              <p className="admin-staff-page__modal-text admin-staff-page__modal-text--muted">
                Esta acción es <strong>irreversible</strong>: se borra la cuenta de autenticación y su ficha
                interna. El historial existente atribuido al usuario se mantiene pero quedará huérfano.
              </p>
              <label className="admin-staff-page__confirm-field">
                <span className="admin-staff-page__confirm-label">
                  Para confirmar, escribí el email exacto:{' '}
                  <code className="admin-staff-page__confirm-code">{deleteModal.email}</code>
                </span>
                <input
                  type="text"
                  className="admin-staff-page__confirm-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleteModal.email}
                  disabled={saving}
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
            </div>
            <div className="admin-staff-page__modal-actions">
              <button
                type="button"
                className="admin-staff-page__btn admin-staff-page__btn--ghost"
                disabled={saving}
                onClick={closeDeleteModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="admin-staff-page__submit admin-staff-page__submit--danger"
                disabled={
                  saving ||
                  deleteConfirmText.trim().toLowerCase() !== (deleteModal.email || '').trim().toLowerCase()
                }
                onClick={confirmDeleteUser}
              >
                {saving ? 'Eliminando…' : 'Eliminar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {permModal && (
        <div className="admin-staff-page__modal-overlay" onClick={() => !saving && setPermModal(null)}>
          <div className="admin-staff-page__modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-staff-page__modal-header">
              <h3>
                Accesos — {permModal.displayName?.trim() ? `${permModal.displayName.trim()} (${permModal.email})` : permModal.email}
              </h3>
              <button
                type="button"
                className="admin-staff-page__modal-close"
                onClick={() => !saving && setPermModal(null)}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <PermissionEditor
              selected={permModal.draft}
              onChange={(next) => setPermModal((m) => (m ? { ...m, draft: next } : m))}
              disabled={saving}
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button type="button" className="admin-staff-page__submit" disabled={saving} onClick={saveModalPermissions}>
                Guardar permisos
              </button>
              <button type="button" className="admin-staff-page__back" style={{ border: '1px solid' }} disabled={saving} onClick={() => setPermModal(null)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStaffUsersPage;
